import { useState, type FormEvent } from 'react';
import { US_STATES, UserRole, type ProfileDetail, type UpdateProfileInput } from '@teentack/shared';
import { useSkills } from '@/hooks/use-skills';
import { useUpdateProfile } from '@/hooks/use-profile';
import { ErrorNote } from '@/components/Form';
import { Button } from '@/components/ui/Button';
import { Input, SelectInput, Textarea } from '@/components/ui/Input';
import { SkillPicker } from '@/components/signup/SkillPicker';
import { AvailabilityFields } from '@/components/fields/AvailabilityFields';
import {
  availabilityValueFrom,
  toAvailability,
  toWorkPreferences,
  type AvailabilityValue,
} from '@/components/fields/availability-vocabulary';
import { ProfileSection } from './ProfileSection';

/** Blank strings are sent as-is — the api reads '' as "clear this column". */
function initialFields(profile: ProfileDetail) {
  return {
    firstName: profile.firstName,
    lastName: profile.lastName,
    photoUrl: profile.photoUrl ?? '',
    bio: profile.bio ?? '',
    city: profile.city ?? '',
    state: profile.state ?? '',
    zipCode: profile.zipCode ?? '',
    professionalTitle: profile.professionalTitle ?? '',
    organizationName: profile.organizationName ?? '',
    organizationWebsite: profile.organizationWebsite ?? '',
    streetAddress: profile.streetAddress ?? '',
    resumeUrl: profile.resumeUrl ?? '',
  };
}

/**
 * The edit view. It replaces the read view in place rather than opening a
 * dialog, so the page never loses its context — the hero stays put above it.
 */
export function ProfileForm({ profile, onDone }: { profile: ProfileDetail; onDone: () => void }) {
  const skillsQuery = useSkills();
  const mutation = useUpdateProfile();

  const [fields, setFields] = useState(initialFields(profile));
  const [skillIds, setSkillIds] = useState<string[]>(profile.skills.map((skill) => skill.publicId));
  const [work, setWork] = useState<AvailabilityValue>(
    availabilityValueFrom(profile.availability, profile.workPreferences),
  );
  const [error, setError] = useState<string | null>(null);

  const isTeen = profile.role === UserRole.TEEN;
  const isPoster = profile.role === UserRole.JOB_POSTER;

  const set =
    (key: keyof typeof fields) =>
    (event: { target: { value: string } }): void => {
      setFields((current) => ({ ...current, [key]: event.target.value }));
    };

  function buildInput(): UpdateProfileInput {
    const base: UpdateProfileInput = {
      firstName: fields.firstName.trim(),
      lastName: fields.lastName.trim(),
      photoUrl: fields.photoUrl.trim(),
      city: fields.city.trim(),
      // The select only ever offers a US_STATES code or '', which is exactly
      // what the schema accepts; the form itself holds it as a plain string.
      state: fields.state as UpdateProfileInput['state'],
      zipCode: fields.zipCode.trim(),
    };

    if (isTeen) {
      return {
        ...base,
        bio: fields.bio.trim(),
        resumeUrl: fields.resumeUrl.trim(),
        skillIds,
        // Availability is a whole-object column, so an empty form clears it.
        availability: toAvailability(work) ?? {},
        workPreferences: toWorkPreferences(work) ?? {},
      };
    }

    if (isPoster) {
      return {
        ...base,
        bio: fields.bio.trim(),
        professionalTitle: fields.professionalTitle.trim(),
        organizationName: fields.organizationName.trim(),
        organizationWebsite: fields.organizationWebsite.trim(),
        streetAddress: fields.streetAddress.trim(),
      };
    }

    return base;
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);

    if (!fields.firstName.trim() || !fields.lastName.trim()) {
      setError('First and last name are both required');
      return;
    }

    try {
      await mutation.mutateAsync(buildInput());
      onDone();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not save your profile');
    }
  }

  const busy = mutation.isPending;

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-6">
      <ErrorNote message={error} />

      <ProfileSection title="Basic details" description="Your name and photo as people see them.">
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="First name"
              value={fields.firstName}
              onChange={set('firstName')}
              autoComplete="given-name"
              required
            />
            <Input
              label="Last name"
              value={fields.lastName}
              onChange={set('lastName')}
              autoComplete="family-name"
              required
            />
          </div>

          <Input
            label="Photo URL"
            type="url"
            value={fields.photoUrl}
            onChange={set('photoUrl')}
            optional
            placeholder="https://…"
            hint="A link to a photo of you. Uploads arrive in a later release."
          />

          {isPoster && (
            <Input
              label="Professional title"
              value={fields.professionalTitle}
              onChange={set('professionalTitle')}
              optional
              placeholder="Freelance tutor, independent contractor…"
            />
          )}

          {/* Filling in a business name is what makes this account post as an
              organization rather than as a person — guardians see and connect
              with the two differently. [Job Poster step 2, Parent step 26] */}
          {isPoster && (
            <>
              <Input
                label="Business name"
                value={fields.organizationName}
                onChange={set('organizationName')}
                optional
                placeholder="Bright Lawns Landscaping"
                hint="Leave empty if you hire as yourself. Filled in, your profile and jobs lead with the business name."
              />
              <Input
                label="Business website"
                value={fields.organizationWebsite}
                onChange={set('organizationWebsite')}
                optional
                type="url"
                placeholder="https://…"
              />
            </>
          )}

          {(isTeen || isPoster) && (
            <Textarea
              label={isTeen ? 'Short bio' : 'About your work'}
              value={fields.bio}
              onChange={set('bio')}
              optional
              maxLength={300}
              hint={`${fields.bio.length}/300 characters`}
            />
          )}
        </div>
      </ProfileSection>

      <ProfileSection title="Location" description="Used to match you with work nearby.">
        <div className="space-y-5">
          {isPoster && (
            <Input
              label="Street address"
              value={fields.streetAddress}
              onChange={set('streetAddress')}
              optional
              autoComplete="street-address"
              hint="Never shown publicly — used for verification and logistics."
            />
          )}

          <div className="grid gap-4 sm:grid-cols-[1.4fr_1fr_1fr]">
            <Input
              label="City"
              value={fields.city}
              onChange={set('city')}
              optional
              autoComplete="address-level2"
            />
            <SelectInput label="State" value={fields.state} onChange={set('state')} optional>
              <option value="">Select a state</option>
              {US_STATES.map((entry) => (
                <option key={entry.code} value={entry.code}>
                  {entry.name}
                </option>
              ))}
            </SelectInput>
            <Input
              label="ZIP"
              value={fields.zipCode}
              onChange={set('zipCode')}
              optional
              inputMode="numeric"
              placeholder="94105"
              autoComplete="postal-code"
            />
          </div>
        </div>
      </ProfileSection>

      {isTeen && (
        <>
          <ProfileSection title="Skills" description="What job posters search on.">
            <SkillPicker
              skills={skillsQuery.data ?? []}
              selected={skillIds}
              onChange={setSkillIds}
              loading={skillsQuery.isPending}
              failed={skillsQuery.isError}
            />
          </ProfileSection>

          <ProfileSection title="Availability" description="When and how you want to work.">
            <div className="space-y-5">
              <AvailabilityFields value={work} onChange={setWork} />

              <Textarea
                label="Anything else about your availability"
                value={work.notes}
                onChange={(event) => setWork({ ...work, notes: event.target.value })}
                optional
                maxLength={200}
                placeholder="e.g. term-time only, or not before 4pm on school days."
              />

              <Input
                label="Résumé link"
                type="url"
                value={fields.resumeUrl}
                onChange={set('resumeUrl')}
                optional
                placeholder="https://…"
              />
            </div>
          </ProfileSection>
        </>
      )}

      {/* Save bar — sticks to the bottom so it is reachable from any section. */}
      <div className="sticky bottom-4 flex items-center gap-3 rounded-2xl border border-line bg-white/95 p-4 shadow-lift backdrop-blur">
        <Button type="submit" disabled={busy} aria-busy={busy || undefined} className="flex-1">
          {busy ? 'Saving…' : 'Save changes'}
        </Button>
        <Button type="button" variant="outline" onClick={onDone} disabled={busy}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
